import { chakra, Text } from "@chakra-ui/react";
import { motion, AnimatePresence, isValidMotionProp } from "framer-motion";
import { useProfile } from "@/contexts/ProfileContext";
import { useEffect, useState } from "react";
import { shouldForwardProp } from "@chakra-ui/system";
import { makePluralRegex } from "@/utils/string";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) => isValidMotionProp(prop) || shouldForwardProp(prop),
});

const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const Sidebar = () => {
  const [visible, setVisible] = useState(false);
  const { profile } = useProfile();
  const [links, setLinks] = useState([
    {
      name: "Background",
      id: "background",
      active: false,
      count: -1,
    },
    {
      name: "Academics",
      id: "academics",
      active: false,
      count: -1,
    },
    {
      name: "Essay(/s)",
      id: "essays",
      active: false,
      count: profile.essays.length,
    },
    {
      name: "AP / IB Score(/s)",
      id: "ap_scores",
      active: false,
      count: profile.ap_scores.length,
    },
    {
      name: "Award(/s)",
      id: "awards",
      active: false,
      count: profile.awards.length,
    },
    {
      name: "Activit(y/ies)",
      id: "ecs",
      active: false,
      count: profile.ecs.length,
    },
    {
      name: "Additional Info",
      id: "additional_info",
      active: false,
      count: profile.additional_info_wc > 0 ? 1 : 0,
    },
    {
      name: "Q&A(/s)",
      id: "advice",
      active: false,
      count: profile.advice.length,
    },
    {
      name: "Scholarship(/s)",
      id: "scholarships",
      active: false,
      count: profile.scholarships?.length,
    },
    {
      name: "Admissions Eval(/s)",
      id: "ao_notes",
      active: false,
      count: profile.ao_notes?.length,
    },
  ]);

  const onScrollTo = (id) => document.getElementById(id).scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      const element = document.getElementById(link.id);
      if (!element) {
        links.splice(i, 1);
        i--;
      }
    }

    const handleScroll = () => {
      setVisible(window.pageYOffset > 100);
      const copyLinks = [...links];
      let modified = false;
      for (let i = 0; i < links.length; i++) {
        const link = links[i];
        const element = document.getElementById(link.id);
        const oldActive = link.active;
        copyLinks[i].active = isInViewport(element);

        if (oldActive !== copyLinks[i].active) {
          modified = true;
        }
      }
      if (modified) {
        setLinks(copyLinks);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <ChakraBox
          display={{ base: "none", md: "block" }}
          as={motion.div}
          bg="white"
          position="fixed"
          p={3}
          top="50%"
          left="0"
          border="1px solid black"
          transform="translateY(-50%)"
          zIndex={2}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <chakra.h4 cursor="pointer" onClick={() => onScrollTo("pfp")}>
            {profile.display_name}
          </chakra.h4>
          {links.map(({ name, active, id, count }, i) => (
            <Text
              as={motion.p}
              key={i}
              fontSize="md"
              cursor="pointer"
              onClick={() => onScrollTo(id)}
              animate={{ color: active ? "#000" : "#aaa" }}
            >
              {count !== -1 && count} {makePluralRegex(name, count)}
            </Text>
          ))}
        </ChakraBox>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
